import React, { useContext, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { StatusViewsContext } from '../../../StatusViews.context';
import {
  MAX_FILE_SIZE,
  UploadProgressRef,
  useUploadDocumentMutation,
} from '../../../api/useUploadDocumentMutation';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import { UploadProgress } from '../UploadProgress/UploadProgress';
import { RequiredDocumentDTO } from '../../../api/useRequiredDocsQuery';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { Lozenge } from '../../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { useAppealContext } from '../../context/useAppealContext';
import { UploadedDocument } from '../../context/AppealContext';

const UPLOAD_ERROR = 'Upload document failed. Please try again later.';

export interface Props {
  isSecondaryApplicant?: boolean;
  category: {
    categoryDescription: string;
    acceptableDocuments: RequiredDocumentDTO[];
  };
}

export const Document = ({
  isSecondaryApplicant,
  category: { categoryDescription, acceptableDocuments },
}: Props) => {
  const { uploadedDocuments, addUploadedDocument, removeUploadedDocument } = useAppealContext();

  const uploadedDocument = uploadedDocuments[
    isSecondaryApplicant ? 'secondaryApplicant' : 'primaryApplicant'
  ].find(uploadedDoc =>
    acceptableDocuments.some(
      acceptableDoc => acceptableDoc.documentType === uploadedDoc.documentType,
    ),
  );

  const { clientId, accountId } = useContext(StatusViewsContext);

  const [selectedDocument, setSelectedDocument] = useState<{ value: string; label: string } | null>(
    uploadedDocument
      ? { value: uploadedDocument.documentType, label: uploadedDocument.documentDescription }
      : null,
  );

  const uploadProgressRef = useRef<UploadProgressRef>(null);

  const {
    mutateAsync: uploadDocument,
    isLoading: isUploadingDocument,
  } = useUploadDocumentMutation({ uploadProgressRef });

  const { getRootProps, getInputProps, open: openFileUploadDialog } = useDropzone({
    accept: 'image/png, image/jpg, image/jpeg, application/pdf',
    multiple: false,
    noDrag: true,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE * 1e6,

    onDropAccepted: async ([file]) => {
      if (!selectedDocument) {
        throw new Error('No document selected');
      }

      try {
        const { snapId, fileName } = await uploadDocument({
          file,
          clientId,
          accountId,
          documentType: selectedDocument.value,
        });

        addUploadedDocument(
          {
            documentType: selectedDocument.value,
            documentDescription: selectedDocument.label,
            snapId,
            fileName,
          },
          isSecondaryApplicant,
        );
      } catch (error) {
        console.error(error);
        toast.error(UPLOAD_ERROR);
      }
    },
    onDropRejected([fileRejection]) {
      const { file } = fileRejection;

      if (file.size > MAX_FILE_SIZE * 1e6) {
        toast.error(`The file size must be less than ${MAX_FILE_SIZE}MB.`);
        return;
      }

      toast.error(UPLOAD_ERROR);
    },
  });

  const downloadDocument = ({ snapId }: UploadedDocument) => {
    window.open(
      `/Consumer/ManagedAccounts/DownloadDocument?clientId=${clientId}&id=${accountId}&snapId=${snapId}`,
      '_blank',
    );
  };

  const removeDocument = ({ documentType }: UploadedDocument) => {
    removeUploadedDocument(documentType, isSecondaryApplicant);

    setSelectedDocument(null);
  };

  const isUploadDisabled = isUploadingDocument || !!uploadedDocument;

  const documentOptions = acceptableDocuments.map(({ documentType, documentDescription }) => ({
    value: documentType,
    label: documentDescription,
  }));

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontWeight={600}>Issue with {categoryDescription}</Text>

        {uploadedDocument && (
          <Lozenge icon="far fa-check" variantColor="success">
            Verified
          </Lozenge>
        )}
      </Box>

      <Box display="flex" alignItems="flex-end" css="gap: 16px">
        <Select
          label="Document"
          value={
            uploadedDocument
              ? {
                  value: uploadedDocument.documentType,
                  label: uploadedDocument.documentDescription,
                }
              : selectedDocument
          }
          options={documentOptions}
          onChange={opt => setSelectedDocument(opt)}
          disabled={isUploadDisabled}
          css="flex: 1"
        />

        <Box display="flex" justifyContent="flex-end" {...getRootProps()}>
          <input {...getInputProps()} />
          <Button
            variant="secondary"
            onClick={openFileUploadDialog}
            disabled={!selectedDocument || isUploadDisabled}
          >
            Browse
          </Button>
        </Box>
      </Box>

      {isUploadingDocument && <UploadProgress ref={uploadProgressRef} />}

      {!isUploadingDocument && uploadedDocument && (
        <Box display="flex" css="gap: 4px">
          <Box display="flex" alignItems="center">
            <Button variant="link" isTruncated onClick={() => downloadDocument(uploadedDocument)}>
              {uploadedDocument.fileName}
            </Button>
          </Box>

          <IconButton
            icon="far fa-trash-alt"
            variant="subtleInactive"
            onClick={() => removeDocument(uploadedDocument)}
            aria-label="Remove document"
          />
        </Box>
      )}

      <Separator my="12" />
    </>
  );
};
